import { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { mergeRegister } from '@lexical/utils';
import type { LexicalCommand, LexicalEditor } from 'lexical';
import { COMMAND_PRIORITY_NORMAL } from 'lexical';

import { TranscriptContext } from 'js/src/data/transcript/transcript-context-provider';
import { saveTranscriptOperation } from 'js/src/data/transcript/transcript-operations';
import { getEditorFromSceneGroupId } from 'js/src/data/transcript/transcript-selectors';
import {
    CORRECTION_SAVE_COMPLETE_COMMAND,
    SAVE_ACTIVE_CORRECTION_COMMAND,
    START_CORRECT_TEXT_COMMAND,
    TypeOfTranscript,
} from 'js/src/data/transcript/transcript-types';
import { getTranslatedEditorBySceneGroupId } from 'js/src/data/translated-editors';
import type { RootState } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import { useLuminaryOperations } from 'js/src/luminary/luminary-hooks';

export function useLexicalEditor(sceneGroupId: string): LexicalEditor | null {
    const mainEditor = useSelector((state: RootState) =>
        getEditorFromSceneGroupId(state, sceneGroupId),
    );
    const translationEditor = useSelector((state: RootState) =>
        getTranslatedEditorBySceneGroupId(state, sceneGroupId),
    );
    const context = useContext(TranscriptContext);

    if (context.typeOfTranscript === TypeOfTranscript.MEDIA_TRANSCRIPT_TAB) {
        return mainEditor || null;
    } else if (context.typeOfTranscript === TypeOfTranscript.TRANSLATION) {
        return translationEditor || null;
    } else {
        throw new Error('Invalid type of transcript, or TranscriptContext missing');
    }
}

export function useTranscriptOperation<P>(
    command: LexicalCommand<P>,
    sceneGroupId: string,
): (args: P, systemTriggered?: boolean, parentActionGroup?: string, isOriginal?: boolean) => void {
    const editor = useLexicalEditor(sceneGroupId);
    const { saveTranscript } = useLuminaryOperations({
        saveTranscriptOperation,
    });

    return useCallback(
        (args, systemTriggered = true, parentActionGroup = uuid(), isOriginal = false) => {
            if (!editor) {
                throw new Error('Editor not found');
            }
            editor.dispatchCommand(command, args);
            saveTranscript(
                sceneGroupId,
                editor,
                { isOriginal },
                systemTriggered,
                parentActionGroup,
            );
        },
        [editor, command, saveTranscript, sceneGroupId],
    );
}

export function useIsTextCorrecting(sceneGroupId: string): boolean {
    const [isTextCorrecting, setIsTextCorrecting] = useState(false);
    const editor = useLexicalEditor(sceneGroupId);

    useEffect(() => {
        if (!editor) {
            return;
        }
        return mergeRegister(
            editor.registerCommand(
                START_CORRECT_TEXT_COMMAND,
                () => {
                    setIsTextCorrecting(true);
                    return false;
                },
                COMMAND_PRIORITY_NORMAL,
            ),
            editor.registerCommand(
                CORRECTION_SAVE_COMPLETE_COMMAND,
                () => {
                    setIsTextCorrecting(false);
                    return false;
                },
                COMMAND_PRIORITY_NORMAL,
            ),
        );
    }, [editor]);

    return isTextCorrecting;
}

export function useIsTextCorrectSaving(sceneGroupId: string): boolean {
    const [isTextCorrectSaving, setIsTextCorrectSaving] = useState(false);
    const editor = useLexicalEditor(sceneGroupId);

    useEffect(() => {
        if (!editor) {
            return;
        }
        return mergeRegister(
            editor.registerCommand(
                SAVE_ACTIVE_CORRECTION_COMMAND,
                () => {
                    setIsTextCorrectSaving(true);
                    return false;
                },
                COMMAND_PRIORITY_NORMAL,
            ),
            editor.registerCommand(
                CORRECTION_SAVE_COMPLETE_COMMAND,
                () => {
                    setIsTextCorrectSaving(false);
                    return false;
                },
                COMMAND_PRIORITY_NORMAL,
            ),
        );
    }, [editor]);

    return isTextCorrectSaving;
}
